import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { DbOrder } from "@/hooks/useRestaurant";

type Props = {
  orders: DbOrder[];
  onUpdateStatus: (id: string, status: string) => Promise<{ error: any } | undefined>;
  onRefresh: () => Promise<void>;
};

const STATUS_OPTIONS = ["new", "preparing", "ready", "out-for-delivery", "completed", "cancelled"];
const statusColor: Record<string, string> = {
  new: "bg-blue-100 text-blue-700",
  preparing: "bg-orange-100 text-orange-700",
  ready: "bg-green-100 text-green-700",
  "out-for-delivery": "bg-purple-100 text-purple-700",
  completed: "bg-green-200 text-green-800",
  cancelled: "bg-red-100 text-red-700",
};

export default function AdminOrders({ orders, onUpdateStatus, onRefresh }: Props) {
  const { toast } = useToast();

  const handleStatusChange = async (orderId: string, status: string) => {
    await onUpdateStatus(orderId, status);
    toast({ title: `Order updated to ${status}` });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-lg">Orders ({orders.length})</CardTitle>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </CardHeader>
      <CardContent>
        {orders.length === 0 ? (
          <p className="text-muted-foreground text-sm">No orders yet.</p>
        ) : (
          <div className="space-y-3">
            {orders.map(order => {
              const items = Array.isArray(order.items) ? order.items as any[] : [];
              return (
                <div key={order.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-lg">{order.order_number}</span>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${statusColor[order.status] || ""}`}>
                        {order.status}
                      </span>
                      <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">{order.order_type}</span>
                    </div>
                    <span className="font-bold">${Number(order.total).toFixed(2)}</span>
                  </div>

                  {items.length > 0 && (
                    <div className="text-sm text-muted-foreground">
                      {items.map((it: any, i: number) => (
                        <span key={i}>{it.quantity}x {it.name}{i < items.length - 1 ? ", " : ""}</span>
                      ))}
                    </div>
                  )}

                  {order.customer_name && <p className="text-sm">Customer: {order.customer_name}</p>}
                  {order.notes && <p className="text-sm text-muted-foreground italic">Note: {order.notes}</p>}

                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">Update status:</span>
                    <Select value={order.status} onValueChange={(val) => handleStatusChange(order.id, val)}>
                      <SelectTrigger className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    {new Date(order.created_at).toLocaleString()} · {order.payment_method}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
